import * as React from 'react';
import get from 'lodash/get';
import { FormikProps, FieldProps, Field, getIn } from 'formik';
import { Grid, Typography } from '@mui/material';
import { useContent, useContentContext } from '@qbe/dfc-content';
import { Button, Checkbox, FormGroupLabel, Modal } from '@qbe/ads-core';
import { FormCard } from '@qbe/ads-blocks-forms';

import CurrencyTextField from 'components/CurrencyTextField/CurrencyTextField';
import FormSpacer from 'components/FormSpacer/FormSpacer';
import Attachment from 'components/Attachment/Attachment';

import { expenseFormFieldsNumberKeys } from 'contexts/initialState';
import { ExpenseFormFields } from 'contexts/FormProvider';
import { useScrollTop } from 'hooks/useScrollTop';
import { select } from 'resources/customerProperties';
import ExpenseModalContent from './ExpenseModalContent';

import validationSchema from '../schema';

interface Props {
  formik: FormikProps<Partial<ExpenseFormFields>>;
  showModal: boolean;
  onSubmitModal: () => void;
  onCloseModal: () => void;
}

const ExpenseForm = ({ showModal, formik, onSubmitModal, onCloseModal }: Props) => {
  useScrollTop();

  const { errors, touched, setFieldValue, values, setTouched } = formik;
  React.useEffect(() => {
    setTouched({}, false);
  }, [setTouched]);

  const { getTextContent } = useContent({
    context: useContentContext(),
  });

  const Labels = [
    getTextContent('Expense.details.label.rent'),
    getTextContent('Expense.details.label.child'),
    getTextContent('Expense.details.label.otherLoan'),
    getTextContent('Expense.details.label.motor'),
    getTextContent('Expense.details.label.credit'),
    getTextContent('Expense.details.label.living'),
    getTextContent('Expense.details.label.util'),
    getTextContent('Expense.details.label.school'),
    getTextContent('Expense.details.label.hospital'),
    getTextContent('Expense.details.label.insurance'),
    getTextContent('Expense.details.label.other'),
  ];

  const isValid = validationSchema.isValidSync(values);

  const isDisabled = !isValid;
  const getError = (name: keyof ExpenseFormFields) => getIn(touched, name) && getIn(errors, name);

  // Attachment
  const { files = [] } = values;

  const setFileValue = (newFiles: File[]) => {
    setFieldValue('files', newFiles, true);
  };

  const updateFiles = (newFiles: FileList) => setFileValue([...files, ...Object.values(newFiles)]);
  const setFiles = (newFiles: File[]) => setFileValue(newFiles);

  const enableAttachment = getIn(values, 'enableAttachment');
  const maxFileSize = getIn(values, 'maxFileSize');

  // Localized content constants
  const pageHeading = getTextContent('Income.details.formHeading');
  const sectionHeading = select('Expense.details.heading');
  const expenseFormHeadingTitle = getTextContent('Expense.details.formHeading');
  const modalTitle = getTextContent('Expense.modal.title');
  const modalSubmitLabel = getTextContent('Expense.modal.submit');
  const expenseDeclarationHeadingTitle = getTextContent('Expense.details.declaration.heading');
  const expenseDeclarationText = getTextContent('Expense.details.declaration.text');
  const continueLabel = getTextContent('Expense.details.continue');

  return (
    <>
      <Modal
        id="expense-modal"
        title={modalTitle}
        open={showModal}
        primaryBtnProps={{
          label: modalSubmitLabel,
          onClick: onSubmitModal,
        }}
        onClose={onCloseModal}
      >
        <ExpenseModalContent />
      </Modal>

      <FormCard variant="pageTitle" title={pageHeading} description="" />
      <FormCard variant="sectionTitle" title={sectionHeading}>
        <FormGroupLabel title={expenseFormHeadingTitle} alignCenter />
        <FormSpacer type="input" />
        <Grid container xs={12} rowSpacing="3em">
          {expenseFormFieldsNumberKeys.map((keyName, index) => (
            <React.Fragment key={index}>
              <Grid item xs={6} alignSelf="center">
                <Typography align="left" variant="body1">
                  {Labels[index]} *
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Field name={keyName}>
                  {({ field }: FieldProps) => (
                    <CurrencyTextField
                      id={`expense-${index}`}
                      label="&nbsp;"
                      data-track={keyName}
                      data-track-value="mask"
                      error={!!getError(keyName as keyof ExpenseFormFields)}
                      helperText={getError(keyName as keyof ExpenseFormFields)}
                      maxLength={10}
                      {...field}
                    />
                  )}
                </Field>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>

        <FormSpacer type="input" />
        <Attachment
          enabled={enableAttachment}
          files={files}
          setFiles={setFiles}
          updateFiles={updateFiles}
          errorMessage={get(errors, 'files')}
          maxFileSize={maxFileSize}
        />
        <FormSpacer type="input" />

        <FormGroupLabel title={expenseDeclarationHeadingTitle} required alignCenter />
        <FormSpacer type="input" />
        <Field name="declaration">
          {({ field }: FieldProps) => (
            <Checkbox
              id="declaration-check"
              label={expenseDeclarationText}
              data-track="declaration"
              data-track-value="capture"
              {...field}
              checked={get(field, 'value', false)}
            />
          )}
        </Field>

        <FormSpacer type="formgroup" />
        <Button label={continueLabel} type="submit" disabled={isDisabled} />
      </FormCard>
    </>
  );
};

export default ExpenseForm;
